import {
  RefrigerantType,
  RefrigerantTypeList,
  ServiceItem,
  ServiceItemImage,
  ServiceItemList,
  SystemStatus,
  SystemStatusList,
} from '@kalos/kalos-rpc';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ServiceItemImageList } from 'kalos-rpc/src/api/ServiceItem';

import { ServiceItemClientService } from '../../tools/helpers';
import { queryClient } from '../../tools/queryClient';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ServiceFilter = EntityFilter<ServiceItem>;

export const useServiceItemBatchGetQuery = <TSelectData = ServiceItemList,>({
  filter,
  select,
  enabled = true,
  refetchOnWindowFocus = true,
}: {
  filter: ServiceFilter;
  select?: (data: ServiceItemList) => TSelectData;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.serviceItem.root,
      queryKeys.serviceItem.list,
      queryKeys.serviceItem.getHash(filter),
    ],
    queryFn: () => {
      const req = ServiceItem.create(filter);
      return ServiceItemClientService.BatchGet(req);
    },
    select,
    enabled,
    refetchOnWindowFocus,
  });

export const useServiceItemSearchQuery = <TSelectData = ServiceItemList,>({
  filter,
  select,
  enabled = true,
  refetchOnWindowFocus = true,
}: {
  filter: ServiceFilter;
  select?: (data: ServiceItemList) => TSelectData;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.serviceItem.root,
      queryKeys.serviceItem.search,
      queryKeys.serviceItem.getHash(filter),
    ],
    queryFn: () => {
      const req = ServiceItem.create(filter);
      return ServiceItemClientService.Search(req);
    },
    select,
    enabled,
    refetchOnWindowFocus,
  });

export const useServiceItemGetQuery = ({ id, enabled = true }: { id: number; enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.serviceItem.get(id),
    queryFn: () => {
      const req = ServiceItem.create({ id });
      return ServiceItemClientService.Get(req);
    },
    enabled: enabled && !!id,
  });

/** ServiceItem has no batch-by-id-list filter, so fetch a set of ids individually
 * (react-query dedupes/caches each by its `serviceItem.get` key, same as
 * `useServiceItemGetQuery`). */
export const useServiceItemsByIdsQuery = (ids: number[]) =>
  useQueries({
    queries: ids.map((id) => ({
      queryKey: queryKeys.serviceItem.get(id),
      queryFn: () => {
        const req = ServiceItem.create({ id });
        return ServiceItemClientService.Get(req);
      },
    })),
  });

export type ServiceItemImageFilter = EntityFilter<ServiceItemImage>;

export const useServiceItemImageBatchGetQuery = <TSelectData = ServiceItemImageList,>({
  filter,
  select,
  enabled,
}: {
  filter: ServiceItemImageFilter;
  select?: (data: ServiceItemImageList) => TSelectData;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.serviceItemImage.root,
      queryKeys.serviceItemImage.list,
      queryKeys.serviceItemImage.getHash(filter),
    ],
    queryFn: () => {
      const req = ServiceItemImage.create(filter);
      return ServiceItemClientService.BatchGetImage(req);
    },
    select,
    enabled,
  });

export const useServiceItemUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: ServiceItem) => {
      return ServiceItemClientService.Update(req);
    },
    onSuccess: (result) => {
      queryClient.setQueriesData<ServiceItemList>(
        { queryKey: [queryKeys.serviceItem.root, queryKeys.serviceItem.list] },
        (oldData) => {
          // Phone lists keep their pages under the same key in another shape; refreshed below.
          if (!Array.isArray(oldData?.results)) return oldData;
          if (oldData) {
            if (oldData.results.find((item) => item.id === result.id)) {
              return ServiceItemList.create({
                ...oldData,
                results: oldData.results.map((item) => (item.id === result.id ? result : item)),
              });
            }
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItem.root, queryKeys.serviceItem.list, 'pages'],
      });
    },
  });
};

export const useServiceItemCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: ServiceItem) => {
      return ServiceItemClientService.Create(req);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItem.root, queryKeys.serviceItem.list],
      });
    },
  });
};

export const useServiceItemDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: ServiceItem) => {
      return ServiceItemClientService.Delete(req);
    },
    onSuccess(data) {
      queryClient.setQueriesData<ServiceItemList>(
        { queryKey: [queryKeys.serviceItem.root, queryKeys.serviceItem.list] },
        (oldData) => {
          // Phone lists keep their pages under the same key in another shape; refreshed below.
          if (!Array.isArray(oldData?.results)) return oldData;
          if (oldData && oldData.results.find((item) => item.id === data.id)) {
            return ServiceItemList.create({
              results: oldData.results.filter((item) => item.id !== data.id),
              totalCount: oldData.totalCount - 1,
            });
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItem.root, queryKeys.serviceItem.list, 'pages'],
      });
    },
  });
};

export const useServiceItemImageCreateMutation = () => {
  return useMutation({
    mutationFn: (arg: Parameters<typeof ServiceItemClientService.CreateServiceItemImage>[0]) =>
      ServiceItemClientService.CreateServiceItemImage(arg),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItemImage.root, queryKeys.serviceItemImage.list],
      });
      return data;
    },
  });
};

export const useServiceItemImageDeletionMutation = () => {
  return useMutation({
    mutationFn: (arg: Parameters<typeof ServiceItemClientService.DeleteImage>[0]) =>
      ServiceItemClientService.DeleteImage(arg),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItemImage.root, queryKeys.serviceItemImage.list],
      });
      return data;
    },
  });
};

export const useServiceItemImageBatchGetMutation = () => {
  return useMutation({
    mutationFn: (arg: Parameters<typeof ServiceItemClientService.BatchGetImage>[0]) =>
      ServiceItemClientService.BatchGetImage(arg),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.serviceItemImage.root, queryKeys.serviceItemImage.list],
      });
      return data;
    },
  });
};

export type SystemStatusFilter = EntityFilter<SystemStatus>;

export const useSystemStatusBatchGetQuery = <TSelectData = SystemStatusList,>({
  filter,
  select,
  enabled = true,
}: {
  filter: SystemStatusFilter;
  select?: (data: SystemStatusList) => TSelectData;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.systemStatus.root,
      queryKeys.systemStatus.list,
      queryKeys.systemStatus.getHash(filter),
    ],
    queryFn: async () => {
      const req = SystemStatus.create(filter);
      const result = await ServiceItemClientService.BatchGetSystemStatus(req);
      return result ?? SystemStatusList.create();
    },
    select,
    enabled,
  });

export type RefrigerantTypeFilter = EntityFilter<RefrigerantType>;

export const useRefrigerantTypeBatchGetQuery = <TSelectData = RefrigerantTypeList,>({
  filter,
  select,
  enabled = true,
}: {
  filter: RefrigerantTypeFilter;
  select?: (data: RefrigerantTypeList) => TSelectData;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.refrigerantType.root,
      queryKeys.refrigerantType.list,
      queryKeys.refrigerantType.getHash(filter),
    ],
    queryFn: async () => {
      const req = RefrigerantType.create(filter);
      const result = await ServiceItemClientService.BatchGetRefrigerantType(req);
      return result ?? RefrigerantTypeList.create();
    },
    select,
    enabled,
  });
